import React from "react";
import PropTypes from "prop-types";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "components/common/Flex";
import { PieChart } from "echarts/charts";
import * as echarts from "echarts/core";
import BasicECharts from "components/common/BasicEChart";
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useAppContext } from "providers/AppProvider";

// Register ECharts components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
]);

// Gross Amount Data
const grossAmountData = [
  { name: "0 Amount", value: 0, color: "#6c757d" },
  { name: "$865", value: 14.92, color: "#20c997" }, // teal green
  { name: "$1.5k", value: 26.47, color: "#0dcaf0" }, // light blue
  { name: "$3.4k", value: 58.61, color: "#fd7e14" }, // orange
];

const grossRadius = ["50%", "70%"];
const grossTotal = "$5.8k";

// ECharts options
const getOptions = (getThemeColor, data, radius, total) => ({
  color: data.map((item) => item.color),
  tooltip: {
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getThemeColor("gray-100"),
    borderColor: getThemeColor("gray-300"),
    textStyle: { color: getThemeColor("gray-1100") },
    borderWidth: 1,
    formatter: (params) =>
      `<strong>${params.data.name}:</strong> ${params.percent.toFixed(2)}%`,
  },
  series: [
    {
      name: "Total",
      type: "pie",
      radius,
      avoidLabelOverlap: false,
      emphasis: { scale: false },
      itemStyle: {
        borderWidth: 2,
        borderColor: getThemeColor("gray-100"),
      },
      label: {
        show: true,
        position: "center",
        formatter: `{a|${total}}`,
        rich: {
          a: {
            fontSize: 14,
            color: getThemeColor("gray-1100"),
            lineHeight: 15,
          },
        },
      },
      data,
    },
  ],
});

// Legend Item
const GrossAmountItem = ({ item }) => {
  const { name, color, value } = item;
  const percentage = value.toFixed(2);
  return (
    <Flex
      alignItems="center"
      justifyContent="between"
      className="fw-semibold fs-11 mb-1"
    >
      <p className="mb-0 d-flex align-items-center text-capitalize">
        <FontAwesomeIcon icon="circle" className="me-2" style={{ color }} />
        {name}
      </p>
      <div>{percentage}%</div>
    </Flex>
  );
};

// Main Component
const ClaimsGrossAmountDoughnutChart = ({
  data = grossAmountData,
  radius = grossRadius,
  totalLabel = grossTotal,
}) => {
  const { getThemeColor } = useAppContext();

  return (
    <Col xs={12} md={6} xl={3} className="">
      <Card className="border-0 shadow-sm h-100">
        <Card.Body>
          <Card.Title className="text-800 fw-bold fs-10">
            Gross Amount
          </Card.Title>
          <Row className="align-items-center">
            {/* Left – Legend */}
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              {data.map((item, index) => (
                <GrossAmountItem item={item} key={index} />
              ))}
            </Col>

            {/* Right – Chart */}
            <Col xs={12} md={6} className="text-center">
              <BasicECharts
                echarts={echarts}
                options={getOptions(getThemeColor, data, radius, totalLabel)}
                style={{
                  width: "10.5rem",
                  height: "10.5rem",
                  margin: "0 auto",
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

// PropTypes
GrossAmountItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
  }),
};

ClaimsGrossAmountDoughnutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
  radius: PropTypes.arrayOf(PropTypes.string),
  totalLabel: PropTypes.string,
};

ClaimsGrossAmountDoughnutChart.defaultProps = {
  data: grossAmountData,
  radius: grossRadius,
  totalLabel: grossTotal,
};

export default ClaimsGrossAmountDoughnutChart;
